import React, { useEffect, useRef, useState, useCallback } from 'react';
import MembershipCard from '../../components/portal/MembershipCard';
import { Crown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import { usePortalTheme } from '../../context/PortalThemeContext';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import PasswordStrengthMeter, { evaluatePassword } from '../../components/PasswordStrengthMeter';
import TwoFactorSection from '../../components/TwoFactorSection';
import PortalLayout from './PortalLayout';
import {
  User as UserIcon, Lock, Bell, Globe, Camera, Save, Loader2,
  Sun, Moon, CreditCard, CheckCircle2, XCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const muted = { color: 'var(--p-muted)' };
const faint = { color: 'var(--p-faint)' };

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Dubai',
  'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney', 'UTC',
];

const Section = ({ icon: Icon, title, extra, children, testid }) => (
  <section className="p-card rounded-xl p-6" data-testid={testid}>
    <div className="flex items-center gap-2 mb-4">
      <Icon size={16} className="text-[#1D4ED8]" />
      <h2 className="font-bold" style={{ color: 'var(--p-text)' }}>{title}</h2>
      {extra}
    </div>
    {children}
  </section>
);

const fmtDate = (ts) => {
  if (!ts) return null;
  const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

export default function ClientSettings() {
  const { user, checkAuth } = useAuth();
  const { theme, setTheme } = usePortalTheme();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingNotif, setSavingNotif] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const [profile, setProfile] = useState({ name: '', email: '', phone: '', timezone: 'America/New_York', avatar_url: '' });
  const [notifications, setNotifications] = useState({
    email_session_reminders: true, email_task_updates: true,
    email_announcements: true, sms_session_reminders: false,
  });
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });

  const [subs, setSubs] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const loadSubs = useCallback(() => {
    axios.get(`${API_URL}/api/subscriptions/me`, { withCredentials: true })
      .then((r) => setSubs(r.data.subscriptions || []))
      .catch(() => setSubs([]));
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/api/portal/settings`, { withCredentials: true })
      .then((res) => {
        if (res.data?.profile) setProfile(res.data.profile);
        if (res.data?.notifications) setNotifications(res.data.notifications);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    loadSubs();
  }, [loadSubs]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/portal/settings/profile`, {
        name: profile.name, phone: profile.phone, timezone: profile.timezone,
      }, { withCredentials: true });
      toast.success('Profile updated');
      if (checkAuth) await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally { setSaving(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) { toast.error('New passwords do not match'); return; }
    if (!evaluatePassword(pwForm.new_password).isStrong) { toast.error('Please choose a stronger password (meet at least 4 of 5 criteria)'); return; }
    setSavingPassword(true);
    try {
      await axios.post(`${API_URL}/api/portal/settings/password`, {
        current_password: pwForm.current_password, new_password: pwForm.new_password,
      }, { withCredentials: true });
      toast.success('Password updated');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update password');
    } finally { setSavingPassword(false); }
  };

  const toggleNotification = async (key, value) => {
    const next = { ...notifications, [key]: value };
    setNotifications(next);
    setSavingNotif(true);
    try {
      await axios.put(`${API_URL}/api/portal/settings/notifications`, next, { withCredentials: true });
      toast.success('Preferences saved');
    } catch {
      toast.error('Failed to save preferences');
      setNotifications(notifications);
    } finally { setSavingNotif(false); }
  };

  const handleAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 300 * 1024) { toast.error('Image must be 300KB or smaller'); return; }
    setUploadingAvatar(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await axios.post(`${API_URL}/api/portal/settings/avatar`, { avatar_data_url: dataUrl }, { withCredentials: true });
      setProfile((p) => ({ ...p, avatar_url: res.data.avatar_url }));
      toast.success('Avatar updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const cancelSubscription = async () => {
    if (!window.confirm('Cancel your subscription? You will keep full access until the end of your current billing period.')) return;
    setCancelling(true);
    try {
      const r = await axios.post(`${API_URL}/api/subscriptions/me/cancel`, {}, { withCredentials: true });
      const until = fmtDate(r.data.current_period_end);
      toast.success(until ? `Cancelled. You keep access until ${until}` : 'Subscription will cancel at period end');
      loadSubs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel subscription');
    } finally { setCancelling(false); }
  };

  if (loading) {
    return (
      <PortalLayout active={null}>
        <div className="flex items-center justify-center py-24">
          <div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </PortalLayout>
    );
  }

  const activeSubs = (subs || []).filter((s) => s.active);

  return (
    <PortalLayout active={null}>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="client-settings">
        <div>
          <span className="text-xs tracking-[0.2em] uppercase text-[#F97316] font-semibold p-eyebrow">Account</span>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-light mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="settings-title">Settings</h1>
          <p className="text-sm mt-1" style={muted}>Manage your profile, security, appearance and subscription.</p>
        </div>

        {/* Appearance */}
        <Section icon={theme === 'dark' ? Moon : Sun} title="Appearance" testid="settings-appearance-section">
          <p className="text-sm mb-4" style={muted}>Choose how your portal looks. Light is the default.</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { id: 'light', label: 'Light', icon: Sun },
              { id: 'dark', label: 'Dark', icon: Moon },
            ].map((opt) => {
              const selected = theme === opt.id;
              return (
                <button key={opt.id} type="button" onClick={() => setTheme(opt.id)} data-testid={`settings-theme-${opt.id}`}
                  className="rounded-xl p-4 border-2 flex items-center gap-3 transition-all text-left"
                  style={{ borderColor: selected ? '#1D4ED8' : 'var(--p-border)', background: selected ? 'rgba(29,78,216,0.06)' : 'var(--p-surface)' }}>
                  <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: opt.id === 'dark' ? '#0F172A' : '#F1F5F9', color: opt.id === 'dark' ? '#fff' : '#0A0F1C' }}>
                    <opt.icon size={17} />
                  </span>
                  <span className="font-semibold" style={{ color: 'var(--p-text)' }}>{opt.label}</span>
                  {selected && <CheckCircle2 size={16} className="text-[#1D4ED8] ml-auto" />}
                </button>
              );
            })}
          </div>
        </Section>

        <Section icon={Crown} title="Membership" testid="settings-membership-section">
          <MembershipCard />
        </Section>

        {/* Subscription */}
        <Section icon={CreditCard} title="Subscription" testid="settings-subscription-section">
          {subs === null ? (
            <div className="flex items-center gap-2 text-sm" style={faint}><Loader2 size={14} className="animate-spin" /> Loading…</div>
          ) : activeSubs.length === 0 ? (
            user?.is_studio_approved ? (
              <div className="flex items-start gap-2 text-sm" style={muted} data-testid="settings-studio-granted">
                <CheckCircle2 size={16} className="text-green-600 mt-0.5 shrink-0" />
                <span>Your Social Studio access is <span className="font-semibold" style={{ color: 'var(--p-text)' }}>active</span>, granted by DCA Enterprises. There is no recurring charge to manage here.</span>
              </div>
            ) : (
              <p className="text-sm" style={muted} data-testid="settings-no-subscription">You don't have an active subscription. When you subscribe (e.g. Social Studio Access), you'll manage it here.</p>
            )
          ) : (
            <div className="space-y-3" data-testid="settings-subscription-list">
              {activeSubs.map((s) => (
                <div key={s.addon_key} className="rounded-xl p-4 border flex flex-col sm:flex-row sm:items-center justify-between gap-3" style={{ borderColor: 'var(--p-border)', background: 'var(--p-surface-2)' }}>
                  <div>
                    <div className="font-semibold flex items-center gap-2" style={{ color: 'var(--p-text)' }}>
                      {s.name}
                      {s.cancel_at_period_end
                        ? <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600">Ending</span>
                        : <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-600">Active</span>}
                    </div>
                    <div className="text-xs mt-0.5" style={muted}>
                      {s.amount != null && <>${Number(s.amount).toFixed(2)}/mo · </>}
                      {s.cancel_at_period_end
                        ? <>Access until {fmtDate(s.current_period_end) || 'end of period'}</>
                        : (s.current_period_end ? <>Renews {fmtDate(s.current_period_end)}</> : 'Renews monthly')}
                    </div>
                  </div>
                  {s.cancel_at_period_end ? (
                    <span className="text-xs inline-flex items-center gap-1.5" style={faint}><XCircle size={14} /> Cancels at period end</span>
                  ) : (
                    <Button type="button" onClick={cancelSubscription} disabled={cancelling}
                      className="bg-white border border-red-200 text-red-600 hover:bg-red-50" data-testid="settings-cancel-subscription">
                      {cancelling ? <><Loader2 size={14} className="animate-spin mr-2" /> Cancelling…</> : 'Cancel subscription'}
                    </Button>
                  )}
                </div>
              ))}
              <p className="text-xs" style={faint}>Cancelling keeps your access until the end of the month you've already paid for. Upgrade options are coming soon.</p>
            </div>
          )}
        </Section>

        {/* Profile Photo */}
        <Section icon={Camera} title="Profile Photo" testid="settings-avatar-section">
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-full overflow-hidden flex items-center justify-center border" style={{ background: 'var(--p-surface-2)', borderColor: 'var(--p-border)' }} data-testid="settings-avatar-preview">
              {profile.avatar_url ? <img src={profile.avatar_url} alt="avatar" className="w-full h-full object-cover" /> : <UserIcon size={28} style={faint} />}
            </div>
            <div className="space-y-2">
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarFile} className="hidden" data-testid="settings-avatar-input" />
              <Button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingAvatar} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" data-testid="settings-avatar-upload-btn">
                {uploadingAvatar ? <><Loader2 size={14} className="animate-spin mr-2" /> Uploading…</> : 'Upload new photo'}
              </Button>
              <p className="text-xs" style={faint}>PNG or JPG, max 300KB.</p>
            </div>
          </div>
        </Section>

        {/* Profile */}
        <Section icon={UserIcon} title="Profile" testid="settings-profile-section">
          <form onSubmit={saveProfile} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--p-text)' }}>Full name</label>
              <Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="settings-profile-name" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--p-text)' }}>Email</label>
              <Input value={profile.email} disabled className="bg-[#F8FAFC] text-[#475569]" data-testid="settings-profile-email" />
              <p className="text-xs mt-1" style={faint}>Contact David to change your email address.</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--p-text)' }}>Phone</label>
              <Input value={profile.phone || ''} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="(555) 555-1234" data-testid="settings-profile-phone" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 flex items-center gap-1" style={{ color: 'var(--p-text)' }}><Globe size={14} /> Timezone</label>
              <select value={profile.timezone || 'America/New_York'} onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}
                className="w-full border border-[#E2E8F0] rounded-md px-3 py-2 text-sm bg-white text-[#0A0F1C]" data-testid="settings-profile-timezone">
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            <Button type="submit" disabled={saving} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" data-testid="settings-profile-save-btn">
              {saving ? <><Loader2 size={14} className="animate-spin mr-2" /> Saving…</> : <><Save size={14} className="mr-2" /> Save profile</>}
            </Button>
          </form>
        </Section>

        {/* Password */}
        <Section icon={Lock} title="Change Password" testid="settings-password-section">
          <form onSubmit={savePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--p-text)' }}>Current password</label>
              <Input type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} data-testid="settings-password-current" autoComplete="current-password" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--p-text)' }}>New password</label>
              <Input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} data-testid="settings-password-new" autoComplete="new-password" />
              <PasswordStrengthMeter password={pwForm.new_password} testId="settings-password-strength" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--p-text)' }}>Confirm new password</label>
              <Input type="password" value={pwForm.confirm_password} onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })} data-testid="settings-password-confirm" autoComplete="new-password" />
            </div>
            <Button type="submit" disabled={savingPassword} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" data-testid="settings-password-save-btn">
              {savingPassword ? <><Loader2 size={14} className="animate-spin mr-2" /> Updating…</> : <><Save size={14} className="mr-2" /> Update password</>}
            </Button>
          </form>
        </Section>

        {/* Two-Factor */}
        <TwoFactorSection />

        {/* Notifications */}
        <Section icon={Bell} title="Notification Preferences" extra={savingNotif && <Loader2 size={12} className="animate-spin" style={faint} />} testid="settings-notifications-section">
          <div className="divide-y" style={{ borderColor: 'var(--p-border)' }}>
            {[
              { key: 'email_session_reminders', title: 'Email session reminders', desc: 'Get email reminders 24h before each call.' },
              { key: 'email_task_updates', title: 'Email task updates', desc: 'When David assigns or completes a task on your plan.' },
              { key: 'email_announcements', title: 'Email announcements', desc: 'Occasional updates about new programs and events.' },
              { key: 'sms_session_reminders', title: 'SMS session reminders', desc: 'Text reminders 1h before each call (requires phone number).' },
            ].map((n) => (
              <div key={n.key} className="py-3 flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-medium" style={{ color: 'var(--p-text)' }}>{n.title}</div>
                  <div className="text-xs" style={muted}>{n.desc}</div>
                </div>
                <Switch checked={!!notifications[n.key]} onCheckedChange={(v) => toggleNotification(n.key, v)} data-testid={`settings-notif-${n.key}`} />
              </div>
            ))}
          </div>
        </Section>
      </div>
    </PortalLayout>
  );
}
